import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Observable, Subject } from 'rxjs';
import { Constants } from '../../../providers/constants/constants';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken as getFirebaseToken, onMessage, isSupported, Messaging } from 'firebase/messaging';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PushService {
  constants = Constants;
  public token: string;
  private firebaseApp?: FirebaseApp;
  private messaging?: Messaging;

  /**
   * Emite cuando existe un token FCM/APNs válido. Suscribirse para registrar/re-registrar
   * listeners de primer plano (p. ej. tras conceder permisos en Configuración).
   */
  private readonly pushTokenReadySubject = new Subject<void>();
  readonly pushTokenReady$ = this.pushTokenReadySubject.asObservable();

  constructor() { }

  private notifyPushTokenReady(): void {
    this.debugLog('notifyPushTokenReady: token disponible; re-registrar listeners de primer plano');
    this.pushTokenReadySubject.next();
  }

  /**
   * Logs de diagnóstico; solo emite si environment.debugPush es true.
   */
  debugLog(...args: unknown[]): void {
    if (!environment.debugPush) {
      return;
    }
    console.log('[Push]', ...args);
  }

  /**
   * Solicita permiso y obtiene token FCM (web) o APNs/FCM nativo (Capacitor).
   * Llamar tras un gesto del usuario mejora la tasa de éxito en navegadores móviles.
   */
  async requestPushActivationFromUser(): Promise<string> {
    this.debugLog('requestPushActivationFromUser: inicio (gesto usuario)');
    const token = await this.getToken();
    this.debugLog(
      'requestPushActivationFromUser: fin',
      token ? `token ok (${token.length} caracteres)` : 'sin token'
    );
    return token;
  }

  /**
   * Obtiene el token del dispositivo.
   * - **Web:** usa `Notification` + FCM (no aplica `PushNotifications` de Capacitor).
   * - **iOS/Android:** ya llama a `PushNotifications.requestPermissions()` antes de registrar; no hace falta duplicarlo en `AppComponent`.
   */
  public async getToken(): Promise<string> {
    const platform = Capacitor.getPlatform();
    this.debugLog('getToken: plataforma', platform);

    if (platform === 'web') {
      const token = await this.getWebToken();
      if (token) {
        this.notifyPushTokenReady();
      }
      return token;
    }

    const permStatus = await PushNotifications.requestPermissions();
    this.debugLog('getToken: nativo requestPermissions', permStatus);

    if (permStatus.receive !== 'granted') {
      this.debugLog('getToken: nativo permiso denegado o no concedido');
      return '';
    }

    const token = await new Promise<string>((resolve) => {
      const registrationHandler = (t: Token) => {
        this.debugLog('getToken: nativo registration', 'token recibido');
        this.token = t.value;
        localStorage.setItem(this.constants.localStorage.tokenDispositivo, this.token);
        PushNotifications.removeAllListeners().catch(() => {});
        this.registerBaseListeners();
        resolve(this.token);
      };

      PushNotifications.addListener('registration', registrationHandler);
      PushNotifications.addListener('registrationError', (error) => {
        this.debugLog('getToken: nativo registrationError', error);
        console.error('[Push] registrationError', error);
        resolve('');
      });
      PushNotifications.register().catch((err) => {
        this.debugLog('getToken: nativo register() falló', err);
        console.error('[Push] PushNotifications.register', err);
        resolve('');
      });
    });

    if (token) {
      this.notifyPushTokenReady();
    }
    return token;
  }

  /** Escuchar notificaciones push (FCM web foreground o Capacitor). */
  public onMessageReceived(): Observable<any> {
    if (Capacitor.getPlatform() === 'web') {
      return new Observable((subscriber) => {
        this.debugLog('onMessageReceived: web, obteniendo Messaging...');
        this.getWebMessaging().then((messaging) => {
          if (!messaging) {
            this.debugLog('FCM web: getWebMessaging devolvió null; no se registró onMessage');
            subscriber.error(new Error('FCM_WEB_MESSAGING_UNAVAILABLE'));
            return;
          }
          const unsubscribe = onMessage(messaging, (payload) => {
            const mapped = {
              title: payload.notification?.title || '',
              body: payload.notification?.body || '',
              data: payload.data || {},
              tap: 'foreground'
            };
            this.debugLog('onMessage payload (web foreground)', { title: mapped.title, hasData: !!payload.data });
            subscriber.next(mapped);
          });

          this.debugLog('onMessage listener activo (web)');
          subscriber.add(() => {
            unsubscribe();
          });
        }).catch((err) => {
          this.debugLog('onMessageReceived: web error al inicializar', err);
          console.error('[Push] onMessageReceived web', err);
          subscriber.error(err);
        });
      });
    }

    this.debugLog('onMessageReceived: nativo, registrando listeners Capacitor');
    return new Observable((subscriber) => {
      const receivedHandler = (notification: PushNotificationSchema) => {
        const mapped = {
          title: notification.title,
          body: notification.body,
          data: notification.data,
          tap: 'foreground'
        };
        subscriber.next(mapped);
      };

      const actionHandler = (action: ActionPerformed) => {
        const n = action.notification;
        const mapped = {
          title: n.title,
          body: n.body,
          data: n.data,
          tap: 'background'
        };
        subscriber.next(mapped);
      };

      PushNotifications.addListener('pushNotificationReceived', receivedHandler);
      PushNotifications.addListener('pushNotificationActionPerformed', actionHandler);

      return () => {
        PushNotifications.removeAllListeners().catch(() => {});
      };
    });
  }

  private registerBaseListeners() {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }
    PushNotifications.addListener('pushNotificationActionPerformed', (_action) => { /* no-op */ });
  }

  private async getWebToken(): Promise<string> {
    try {
      const supported = await isSupported();
      this.debugLog('getWebToken: isSupported(FCM)', supported);

      if (!supported || typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
        this.debugLog(
          'getWebToken: entorno no soportado',
          { supported, hasWindow: typeof window !== 'undefined', hasNotification: typeof window !== 'undefined' && 'Notification' in window, hasSW: typeof navigator !== 'undefined' && 'serviceWorker' in navigator }
        );
        return '';
      }

      if (typeof Notification !== 'undefined') {
        this.debugLog('getWebToken: Notification.permission (antes de pedir)', Notification.permission);
      }

      if (!environment?.firebase?.vapidKey) {
        console.warn('[Push] Firebase vapidKey no configurada para web.');
        return '';
      }

      const permission = await Notification.requestPermission();
      this.debugLog('getWebToken: resultado requestPermission', permission);

      if (permission !== 'granted') {
        this.debugLog('getWebToken: permiso no concedido; si queda "default" prueba activar desde Configuración (gesto usuario)');
        return '';
      }

      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      this.debugLog('getWebToken: service worker registrado', registration?.scope);

      const messaging = await this.getWebMessaging();
      if (!messaging) {
        this.debugLog('getWebToken: getWebMessaging null tras SW');
        return '';
      }

      const token = await getFirebaseToken(messaging, {
        vapidKey: environment.firebase.vapidKey,
        serviceWorkerRegistration: registration
      });

      if (!token) {
        this.debugLog('getWebToken: getFirebaseToken devolvió vacío');
        return '';
      }

      this.token = token;
      localStorage.setItem(this.constants.localStorage.tokenDispositivo, this.token);
      await this.copyTokenToClipboard(this.token);
      this.debugLog('getWebToken: token guardado');
      return token;
    } catch (error) {
      this.debugLog('getWebToken: excepción', error);
      console.error('[Push] getWebToken', error);
      return '';
    }
  }

  private async getWebMessaging(): Promise<Messaging | null> {
    try {
      const supported = await isSupported();
      if (!supported) {
        this.debugLog('getWebMessaging: isSupported false');
        return null;
      }

      if (!this.firebaseApp) {
        this.firebaseApp = getApps().length ? getApps()[0] : initializeApp(environment.firebase);
      }

      if (!this.messaging) {
        this.messaging = getMessaging(this.firebaseApp);
      }

      return this.messaging || null;
    } catch (error) {
      this.debugLog('getWebMessaging: error', error);
      console.error('[Push] getWebMessaging', error);
      return null;
    }
  }

  /**
   * Copia el token al portapapeles para facilitar pruebas/manual QA.
   * No interrumpe el flujo si el navegador bloquea el acceso al clipboard.
   */
  private async copyTokenToClipboard(token: string): Promise<void> {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(token);
        this.debugLog('copyTokenToClipboard: token copiado con navigator.clipboard');
        return;
      }
    } catch (error) {
      this.debugLog('copyTokenToClipboard: fallo navigator.clipboard', error);
    }

    try {
      if (typeof document === 'undefined') {
        return;
      }
      const textarea = document.createElement('textarea');
      textarea.value = token;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.debugLog('copyTokenToClipboard: token copiado con fallback execCommand');
    } catch (error) {
      this.debugLog('copyTokenToClipboard: fallback falló', error);
    }
  }
}
